<script setup lang="ts">
import brokerSgIcon from '~/assets/img/exchange-detail/background-investigation/broker-sg.png'
import brokerUkIcon from '~/assets/img/exchange-detail/background-investigation/broker-uk.png'
import brokerUsIcon from '~/assets/img/exchange-detail/background-investigation/broker-us.png'
import brokerVeIcon from '~/assets/img/exchange-detail/background-investigation/broker-ve.png'

type InvestigationTab = {
  key: string
  label: string
  count?: number
}

type InvestigationCompany = {
  name: string
  status: string
  country: string
  registrationNumber: string
  establishedAt: string
  icon: string
}

const activeTabKey = 'companies'

const tabs: InvestigationTab[] = [
  {
    key: 'companies',
    label: '相关企业',
    count: 8,
  },
  {
    key: 'employees',
    label: '相关员工',
    count: 29,
  },
  {
    key: 'genealogy',
    label: '关系族谱',
  },
]

const companies: InvestigationCompany[] = [
  {
    name: 'TRADING POINT OF FINANCIAL INSTRUMENTS PTY LTD',
    status: '在业',
    country: '美国',
    registrationNumber: '141774727',
    establishedAt: '2010-02-01',
    icon: brokerUsIcon,
  },
  {
    name: 'GAIN CAPITAL HOLDINGS, INC.(Delaware (United States))',
    status: '在业',
    country: '英国',
    registrationNumber: '2010401047199',
    establishedAt: '2003-08-01',
    icon: brokerUkIcon,
  },
  {
    name: 'STONEX FINANCIAL LTD(United Kingdom)',
    status: '在业',
    country: '委内瑞拉',
    registrationNumber: '141774727',
    establishedAt: '2010-02-01',
    icon: brokerVeIcon,
  },
  {
    name: 'STONEX FINANCIAL LTD(United Kingdom)',
    status: '在业',
    country: '委内瑞拉',
    registrationNumber: '141774727',
    establishedAt: '2010-02-01',
    icon: brokerVeIcon,
  },
  {
    name: 'TRADING POINT OF FINANCIAL INSTRUMENTS PTY LTD',
    status: '在业',
    country: '新加坡',
    registrationNumber: '05616586',
    establishedAt: '2020-05-30',
    icon: brokerSgIcon,
  },
  {
    name: 'GAIN CAPITAL HOLDINGS, INC.(Delaware (United States))',
    status: '在业',
    country: '委内瑞拉',
    registrationNumber: '3688567',
    establishedAt: '2020-05-30',
    icon: brokerVeIcon,
  },
]
</script>

<template>
  <section class="background-investigation" data-exchange-detail-section="background-investigation">
    <div class="background-investigation-card">
      <div class="background-tabs" role="tablist" aria-label="背景调查">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="background-tab"
          :class="{ 'background-tab-active': tab.key === activeTabKey }"
          role="tab"
          :aria-selected="tab.key === activeTabKey"
          :tabindex="tab.key === activeTabKey ? 0 : -1"
        >
          <span class="background-tab-label">{{ tab.label }}</span>
          <span v-if="tab.count" class="background-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <div class="background-company-grid">
        <article
          v-for="(company, index) in companies"
          :key="`${company.name}-${company.country}-${index}`"
          class="background-company"
        >
          <div class="company-header">
            <CommonImage :src="company.icon" alt="" class="company-icon" width="42" height="42" loading="lazy" />
            <div class="company-identity">
              <h3>{{ company.name }}</h3>
              <div class="company-meta">
                <span class="company-status">{{ company.status }}</span>
                <span class="company-divider" aria-hidden="true"></span>
                <span class="company-country">{{ company.country }}</span>
              </div>
            </div>
          </div>

          <div class="company-facts">
            <div class="company-fact">
              <p>注册编号</p>
              <strong>{{ company.registrationNumber }}</strong>
            </div>
            <div class="company-fact">
              <p>成立时间</p>
              <strong>{{ company.establishedAt }}</strong>
            </div>
          </div>
        </article>
      </div>

      <button type="button" class="background-expand">展开全部</button>
    </div>
  </section>
</template>

<style scoped lang="less">
.background-investigation {
  --background-text: #292a2e;
  --background-text-subtle: #505258;
  --background-text-subtlest: #7d818a;
  --background-count-bg: #f8f8f8;
  --background-card-bg: #f8f8f8;
  --background-link: #0d3f63;
  --background-muted-label: #86909c;
  --background-muted-value: #3d3d3d;
  --background-border: rgba(5, 21, 36, 0.06);
  --background-divider: rgba(11, 18, 40, 0.14);
  --background-regulated: #1f845a;

  width: 100%;
  min-height: 488px;
  color: var(--background-text);
  background: #fff;
}

.background-investigation,
.background-investigation * {
  box-sizing: border-box;
}

.background-investigation-card {
  width: 100%;
  padding: 24px;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px var(--color-border-subtle, rgba(11, 18, 40, 0.14));
}

.background-tabs {
  display: flex;
  height: 44px;
  align-items: center;
  gap: 24px;
  border-bottom: 0.5px solid var(--background-border);
}

.background-tab {
  position: relative;
  display: inline-flex;
  height: 44px;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 0 0 16px;
  border: 0;
  background: transparent;
  color: var(--background-text-subtlest);
  cursor: default;
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  white-space: nowrap;
}

.background-tab-active {
  color: var(--background-text);
  font-weight: 600;

  &::after {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 28px;
    height: 4px;
    border-radius: 999px;
    background: var(--background-text);
    content: '';
    transform: translateX(-50%);
  }
}

.background-tab-count {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-top: 5px;
  padding: 0 4px;
  border-radius: 1000px;
  background: var(--background-count-bg);
  color: var(--background-text-subtlest);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.background-company-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 368px));
  gap: 24px;
  align-items: start;
  padding-top: 20px;
}

.background-company {
  display: flex;
  width: 368px;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
}

.company-header {
  display: flex;
  width: 100%;
  min-height: 68px;
  align-items: flex-start;
  gap: 12px;
}

.company-icon {
  display: block;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  object-fit: contain;
  transform: translate(-1px, -1px);
}

.company-identity {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
}

.company-identity h3 {
  display: -webkit-box;
  min-height: 24px;
  margin: 0;
  overflow: hidden;
  color: var(--background-text);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.company-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.company-status {
  color: var(--background-regulated);
}

.company-divider {
  width: 1px;
  height: 8px;
  background: var(--background-divider);
}

.company-country {
  color: var(--background-text-subtle);
}

.company-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  min-height: 78px;
  padding: 16px;
  border-radius: 8px;
  background: var(--background-card-bg);
}

.company-fact {
  min-width: 0;
}

.company-fact p,
.company-fact strong {
  margin: 0;
}

.company-fact p {
  color: var(--background-muted-label);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.company-fact strong {
  display: block;
  margin-top: 4px;
  overflow: hidden;
  color: var(--background-muted-value);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.background-expand {
  display: flex;
  width: 100%;
  height: 52px;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  border: 0;
  background: transparent;
  color: var(--background-link);
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .background-investigation {
    min-height: auto;
  }

  .background-company-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .background-company {
    width: 100%;
  }
}

@media screen and (max-width: 799px) {
  .background-investigation {
    min-width: 752px;
    min-height: auto;
  }

  .background-company-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .background-company {
    width: 100%;
  }
}
</style>
